import { Channel } from "phoenix";
import * as appMessageService from "common/api/appMessage.service";
import { IAppMessageMetadata } from "common/api/appMessage.service";
import { isSupersededChannelError } from "common/api/appMessageChannel.service";
import { addFlashAlert, removeFlashAlertByExternalId } from "common/components/FlashAlert";
import { whenRemoteStorage } from "common/components/RemoteStorage/remoteStorageRegistry";
import { trackEventHelper } from "common/helpers/Analytics";
import {
  MESSAGES_OPEN,
  MESSAGES_PER_PAGE,
  MessagesNavigate,
  getMessageNavigate,
  messagesSearch,
} from "common/helpers/MessagesUrl";
import { claimSurfaced } from "common/helpers/SurfacedMessages";
import { queryParamsFromJSON } from "common/records/QueryParams";
import { IAppMessageRecipientData, appMessageRecipientFromJSON, isRead } from "common/records/AppMessage";
import { ThunkResult } from "common/reducers";
import { AppMessageActions } from "common/reducers/appMessage.actions";
import { model } from "common/reducers/appMessage.reducer";
import { IdsManagementActions } from "common/reducers/idsManagement.actions";
import { handleErrors } from "common/reducers/Utils";
import { appMessage } from "common/selectors/appMessage.selectors";
import { ids, loading, queryParams } from "common/selectors/idsManagement.selectors";
import { RootDispatchType, RootGetState } from "common/store";
import { Nullable } from "common/types";
import { List } from "immutable";

const ANALYTICS_CATEGORY = "Messages";

const track = (action: string) => trackEventHelper({ category: ANALYTICS_CATEGORY, action });

/**
 * The search string of the page the panel is open over. Unaffected by the router's `basename`.
 *
 * @returns
 */
const currentSearch = (): string => window.location.search;

/**
 * Waits for the service and then for its join. The loader fires before React has mounted the provider,
 * so neither step may treat "not there yet" as a failure — the call queues.
 *
 * @param run
 * @returns
 */
const call = async <T>(run: (channel: Channel) => Promise<T>): Promise<T> => {
  const remoteStorage = await whenRemoteStorage();

  return remoteStorage.appMessages.call(run);
};

/**
 * A channel rejection arrives as `{reason}` (or an `Error` on timeout), neither of which `handleErrors`
 * recognises.
 *
 * @param error the raw phoenix rejection
 * @param flash
 */
const handleChannelError = (error: unknown, flash = true): void => {
  const reason = (error as { reason?: string })?.reason ?? (error as Error)?.message;

  handleErrors(reason ? { data: { errors: reason } } : error, flash);
};

const isPending = (message: IAppMessageRecipientData): boolean =>
  !isRead(appMessageRecipientFromJSON(message)) && !message.dismissed_at && !message.archived_at;

const claimForToast = (message: IAppMessageRecipientData): boolean => isPending(message) && claimSurfaced(message.id);

const surfaceToast = (dispatch: RootDispatchType, message: IAppMessageRecipientData): void => {
  addFlashAlert(message.app_message.level, message.app_message.subject, true, {
    id: message.id,
    title: "New Message",
    action: {
      text: "View",
      onClick: () => {
        void dispatch(AppMessageAsyncActions.markAction(message.id, getMessageNavigate()));
      },
    },
    onClose: () => {
      void dispatch(AppMessageAsyncActions.dismissMessage(message.id));
    },
  });
};

export const AppMessageAsyncActions = {
  /**
   * Opening is a navigation; the loader reacts to the parameter and fetches the page.
   *
   * @param navigate
   * @param search the host page's search string, which the panel's parameters are added to
   * @returns
   */
  openMessageCenter: (navigate: MessagesNavigate, search: string): ThunkResult<void> => {
    return () => {
      track("open message center");
      navigate({ search: messagesSearch(search, MESSAGES_OPEN) });
    };
  },

  listMessages: (page: number, perPage: number = MESSAGES_PER_PAGE): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      dispatch(IdsManagementActions.fetchModel(model, queryParamsFromJSON({ page, per_page: perPage })));

      try {
        const response = await call((joined) => appMessageService.listMessages(joined, page, perPage));

        const messages = response.app_messages ?? [];
        const { metadata } = response;

        dispatch(AppMessageActions.receiveAppMessages(messages, metadata));
        dispatch(AppMessageActions.setLoadFailed(false));
        dispatch(
          IdsManagementActions.receiveModel(model, List(messages.map((message) => message.id)), {
            current_page: metadata.page_number ?? page,
            total_pages: metadata.total_pages ?? 1,
            total_count: metadata.total,
            per_page: metadata.per_page ?? perPage,
          }),
        );
      } catch (error) {
        if (isSupersededChannelError(error)) {
          dispatch(IdsManagementActions.setModelLoaded(model));
          return;
        }

        handleChannelError(error);
        dispatch(AppMessageActions.setLoadFailed(true));
        dispatch(IdsManagementActions.receiveModel(model, List()));
      }
    };
  },

  /**
   * @param id the message to expand, or collapse when it is already the expanded one
   * @param navigate
   * @param expanded whether `id` is currently the expanded message
   * @param search the host page's search string, which the panel's parameters are added to
   * @returns
   */
  viewMessage: (
    id: string,
    navigate: MessagesNavigate,
    expanded: boolean,
    search: string,
  ): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      navigate({ search: messagesSearch(search, expanded ? MESSAGES_OPEN : id) }, { replace: true });

      const message = appMessage(getState(), { messageId: id });
      if (expanded || isRead(message)) return;

      track("view message");
      try {
        await call((joined) => appMessageService.markRead(joined, id));
      } catch (error) {
        if (isSupersededChannelError(error)) return;

        handleChannelError(error);
      }
    };
  },

  archiveMessage: (id: string): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      track("archive message");
      try {
        await call((joined) => appMessageService.archiveMessage(joined, id));
      } catch (error) {
        if (!isSupersededChannelError(error)) handleChannelError(error);
        return;
      }

      dispatch(IdsManagementActions.removeId(id, model));
    };
  },

  dismissMessage: (id: string): ThunkResult<Promise<void>> => {
    return async () => {
      try {
        await call((joined) => appMessageService.dismissMessage(joined, id));
      } catch (error) {
        if (isSupersededChannelError(error)) return;

        handleChannelError(error, false);
      }
    };
  },

  /**
   * The toast's "View". Drops the page parameter so the loader fetches the first page, where the
   * newest message is.
   *
   * @param id
   * @param navigate
   * @returns
   */
  markAction: (id: string, navigate: Nullable<MessagesNavigate>): ThunkResult<Promise<void>> => {
    return async () => {
      track("toast view message");
      navigate?.({ search: messagesSearch(currentSearch(), id, { resetPage: true }) });

      try {
        await call((joined) => appMessageService.markAction(joined, id));
      } catch (error) {
        if (isSupersededChannelError(error)) return;

        handleChannelError(error);
      }
    };
  },

  /**
   * Phoenix replays the join reply on every rejoin, and that reply carries only the pending
   * (undismissed, unread) messages, with no page info.
   *
   * @param messages
   * @param metadata
   * @returns
   */
  receiveInitialMessages: (messages: IAppMessageRecipientData[], metadata: IAppMessageMetadata): ThunkResult<void> => {
    return (dispatch: RootDispatchType, getState: RootGetState) => {
      dispatch(AppMessageActions.receiveAppMessages(messages, metadata));

      messages.filter(claimForToast).forEach((message) => surfaceToast(dispatch, message));

      const state = getState();
      if (loading(state, { modelName: model })) return;

      dispatch(AppMessageActions.setLoadFailed(false));

      if (ids(state, { modelName: model }).isEmpty()) {
        dispatch(IdsManagementActions.receiveModel(model, List(messages.map((message) => message.id))));
      }
    };
  },

  receiveNewMessage: (message: IAppMessageRecipientData, metadata: IAppMessageMetadata): ThunkResult<void> => {
    return (dispatch: RootDispatchType, getState: RootGetState) => {
      dispatch(AppMessageActions.addAppMessage(message, metadata));

      const page = queryParams(getState(), { modelName: model })?.page;
      if (!page || page === 1) {
        dispatch(IdsManagementActions.prependId(message.id, model));
      }

      if (claimForToast(message)) surfaceToast(dispatch, message);
    };
  },

  /**
   * rsf-analytics broadcasts to the recipient's channels, but each one pushes the update only when the
   * message's `recipient_access_uuid` is unset or matches that channel's access.
   *
   * @param message
   * @param metadata
   * @returns
   */
  receiveMessageUpdate: (message: IAppMessageRecipientData, metadata: IAppMessageMetadata): ThunkResult<void> => {
    return (dispatch: RootDispatchType) => {
      dispatch(AppMessageActions.updateAppMessage(message, metadata));

      if (message.archived_at) {
        dispatch(IdsManagementActions.removeId(message.id, model));
      }

      if (message.read_at || message.action_at || message.dismissed_at || message.archived_at) {
        removeFlashAlertByExternalId(message.id);
      }
    };
  },
};
